import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { MAX_SELECTED_LOCATIONS } from "./constants";
import { useLocationSelect } from "./hook";
import { LocationSelectModal } from "./Modal";

import "./styles.scss";

interface LocationSelectProps {
  selectedLocations: { id: number; name: string }[];
  setSelectedLocations: (locations: { id: number; name: string }[]) => void;
}

/**
 * Handles selecting multiple facilities from the pool of all facilities (for admin)
 * or all facilities for a specific organization (for org_admin).
 * Handles complex filtering and tracking selected facilities.
 */
export const LocationSelect: React.FC<LocationSelectProps> = ({
  selectedLocations,
  setSelectedLocations,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string, args?: any) =>
    _t(`hookForm.locationSelect.${key}`, args);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    locations,
    changeQueryParams,
    organizations,
    queryParams,
    isLoadingLocations,
    isLoadingOrganizations,
  } = useLocationSelect();

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSetSelectedLocations = (
    locations: { id: number; name: string }[]
  ) => {
    setSelectedLocations(locations);
  };

  const shownSelectedLocations = selectedLocations.slice(
    0,
    MAX_SELECTED_LOCATIONS
  );
  const notShownSelectedLocationsText = selectedLocations
    .slice(MAX_SELECTED_LOCATIONS)
    .map((location) => location.name)
    .join(", ");

  return (
    <>
      <div className="admin-location-select">
        <div className="admin-location-select-list">
          {selectedLocations.length === 0 && (
            <div className="admin-location-select-item not-location">
              {t("no_locations_selected")}
            </div>
          )}
          {shownSelectedLocations.map((location) => (
            <div key={location.id} className="admin-location-select-item">
              {location.name}
            </div>
          ))}
          {selectedLocations.length > MAX_SELECTED_LOCATIONS && (
            <div
              className="admin-location-select-item not-location"
              title={notShownSelectedLocationsText}
            >
              {t("show_more", {
                count: selectedLocations.length - MAX_SELECTED_LOCATIONS,
              })}
            </div>
          )}
          <button
            className="btn btn-border"
            onClick={handleOpenModal}
            type="button"
          >
            {t("edit")}
          </button>
        </div>
      </div>
      {isModalOpen && (
        <LocationSelectModal
          isLoadingLocations={isLoadingLocations}
          isLoadingOrganizations={isLoadingOrganizations}
          allLocations={locations}
          selectedLocations={selectedLocations}
          setSelectedLocations={handleSetSelectedLocations}
          onClose={handleCloseModal}
          changeQueryParams={changeQueryParams}
          queryParams={queryParams}
          organizations={organizations}
        />
      )}
    </>
  );
};
